import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaPrint, FaSync, FaExclamationTriangle, FaRobot } from 'react-icons/fa';
import Sidebar from '../Sidebar/Sidebar';
import { buildApiUrl } from '../../config/api.config';

const SmartPrep = () => {
    const [prepData, setPrepData] = useState([]);
    const [reportId, setReportId] = useState(null);
    const [reportStatus, setReportStatus] = useState('pending');
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [finalizing, setFinalizing] = useState(false);
    const [buffer, setBuffer] = useState(1); // 1 = 0%, 1.1 = 10%, 1.25 = 25%
    
    // Get restaurant ID from local storage (consistent with Sidebar pattern)
    const userDetails = JSON.parse(localStorage.getItem('userDetails'));
    const isMaster = userDetails?.role === 'owner' || localStorage.getItem('fromMaster') === 'true';
    let restaurantId = isMaster 
        ? localStorage.getItem('currentRestaurantId') 
        : (typeof userDetails?.restaurantId === 'object' ? userDetails?.restaurantId?._id : userDetails?.restaurantId);

    // Feature Gating Logic
    const activeFeatures = JSON.parse(localStorage.getItem("activeFeatures") || "[]");
    const hasAccess = isMaster || activeFeatures.includes('AI Smart Prep Engine');

    useEffect(() => {
        if (restaurantId && hasAccess) fetchPrepData();
    }, [restaurantId, hasAccess]);

    const fetchPrepData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(buildApiUrl(`prep/${restaurantId}/today`));
            if (response.data.success && response.data.data) {
                setPrepData(response.data.data.items);
                setReportId(response.data.data._id);
                setReportStatus(response.data.data.status);
                setLastUpdated(response.data.data.updatedAt);
            }
        } catch (error) {
            if (error.response && error.response.status === 404) {
                 setPrepData([]);
                 setReportId(null);
            } else {
                console.error("Error fetching prep data", error);
                toast.error("Failed to load prep list");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        // Regeneration Warning
        if (prepData.length > 0) {
            const confirmText = reportStatus === 'completed' 
                ? "This report is already finalized. You cannot regenerate it."
                : "Regenerating will reset all your current mark-done progress and recalculate from sales. Are you sure?";
            
            if (reportStatus === 'completed') {
                toast.warning(confirmText);
                return;
            }
            if (!window.confirm(confirmText)) return;
        }

        try {
            setGenerating(true);
            const response = await axios.post(buildApiUrl("prep/generate"), { 
                restaurantId,
                bufferFactor: buffer 
            });
            if (response.data.success) {
                const items = response.data.data?.items || [];
                setPrepData(items);
                setReportId(response.data.data._id);
                setReportStatus(response.data.data.status || 'pending');
                setLastUpdated(response.data.data?.updatedAt || new Date());
                toast.success(response.data.message || "Prep list generated!");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Failed to generate prep list";
            toast.error(errorMsg);
        } finally {
            setGenerating(false);
        }
    };

    const handleItemStatusUpdate = async (inventoryId, currentStatus) => {
        const newStatus = currentStatus === 'done' ? 'pending' : 'done';
        try {
            const response = await axios.post(buildApiUrl("prep/update-item"), {
                reportId,
                inventoryId,
                status: newStatus
            });
            if (response.data.success) {
                setPrepData(response.data.data.items);
                toast.success(response.data.message);
            }
        } catch (error) {
            toast.error("Failed to update item status");
        }
    };

    const handleFinalize = async () => {
        if (!window.confirm("This will mark all items as prepared and deduct them from inventory. Proceed?")) return;
        try {
            setFinalizing(true);
            const response = await axios.post(buildApiUrl("prep/finalize"), { reportId });
            if (response.data.success) {
                setPrepData(response.data.data.items);
                setReportStatus('completed');
                toast.success("Prep work finalized!");
            }
        } catch (error) {
            toast.error("Failed to finalize prep report");
        } finally {
            setFinalizing(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    // ... (rest of the component JSX) ...
    // Note: I'll need to make sure the JSX uses these handlers correctly in the next chunk if needed, 
    // but I'll try to include the relevant UI parts here.

    // Inline styles for dark theme consistency
    const styles = {
        container: {
            display: 'flex',
            height: '100vh',
            background: '#1B1C1F',
            color: 'white',
            overflow: 'hidden'
        },
        mainContent: {
            flex: 1,
            padding: '2rem',
            overflowY: 'auto',
            background: '#1B1C1F'
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '2rem',
            flexWrap: 'wrap',
            gap: '1rem'
        },
        title: {
            fontSize: '1.8rem',
            fontWeight: '800',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
        },
        subtitle: {
            color: 'rgba(255,255,255,0.5)',
            marginTop: '0.5rem',
            fontSize: '0.9rem'
        },
        buttonGroup: {
            display: 'flex',
            gap: '0.75rem'
        },
        primaryBtn: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #ff4135 0%, #ff6b6b 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontWeight: '700',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            fontSize: '0.9rem'
        },
        secondaryBtn: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: 'rgba(255,255,255,0.05)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            fontSize: '0.9rem'
        },
        card: {
            background: '#212226',
            borderRadius: '16px',
            padding: '1.5rem',
            border: '1px solid rgba(255,255,255,0.05)'
        },
        emptyState: {
            textAlign: 'center',
            padding: '4rem 2rem',
            background: '#1B1C1F',
            borderRadius: '16px',
            border: '1px dashed rgba(255,255,255,0.1)'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse'
        },
        tableHeader: {
            background: '#292a2e',
            textAlign: 'left'
        },
        th: {
            padding: '1rem 1.5rem',
            color: 'rgba(255,255,255,0.6)',
            fontWeight: '600',
            fontSize: '0.85rem',
            textTransform: 'uppercase',
            letterSpacing: '1px'
        },
        td: {
            padding: '1.25rem 1.5rem',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
            color: 'white'
        },
        infoGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.5rem',
            marginTop: '2rem'
        },
        infoCard: {
            background: '#212226',
            padding: '1.5rem',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.05)'
        },
        infoTitle: {
            fontWeight: '700',
            marginBottom: '0.5rem',
            color: 'white'
        },
        infoText: {
            fontSize: '0.9rem',
            color: 'rgba(255,255,255,0.5)',
            lineHeight: '1.6'
        },
        badge: {
            background: 'rgba(255, 65, 53, 0.15)',
            color: 'white',
            padding: '0.5rem 1rem',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontWeight: '600'
        },
        quantity: {
            fontSize: '1.25rem',
            fontWeight: '800',
            color: '#ff4135'
        }
    };

    if (!hasAccess) {
        return (
            <div style={styles.container}>
                <Sidebar />
                <div style={{ ...styles.mainContent, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                    <div style={{ background: 'rgba(255, 65, 53, 0.1)', padding: '3rem', borderRadius: '24px', border: '1px solid rgba(255, 65, 53, 0.2)', maxWidth: '500px', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
                        <FaRobot style={{ fontSize: '4rem', color: '#ff4135', marginBottom: '1.5rem' }} />
                        <h2 style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '1rem', color: 'white' }}>Feature Locked</h2>
                        <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: '1.6', marginBottom: '2rem', fontSize: '1.05rem' }}>
                            The <strong>AI Smart Prep Engine</strong> is not enabled for your current cluster. 
                            Upgrade to unlock advanced predictive forecasting and reduce daily kitchen waste.
                        </p>
                        <button 
                            onClick={() => window.location.href = '/admin/subscription/plans'}
                            style={{ 
                                ...styles.primaryBtn, 
                                margin: '0 auto',
                                padding: '1rem 2rem',
                                fontSize: '1rem'
                            }}
                        >
                            Unlock Features
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <Sidebar />
            <div style={styles.mainContent}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>
                        <FaRobot style={{ color: 'white' }} />
                        AI Smart Prep Engine
                    </h1>
                    <p style={styles.subtitle}>
                        Daily ingredient forecast based on historical sales trends.
                    </p>
                </div>
                <div style={styles.buttonGroup}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', fontWeight: '600' }}>SAFETY BUFFER</label>
                            <span style={{ 
                                fontSize: '0.6rem', 
                                background: '#ff4135', 
                                color: 'white', 
                                padding: '1px 5px', 
                                borderRadius: '4px',
                                fontWeight: '700',
                                marginLeft: '0.5rem'
                            }}>ONCE A DAY</span>
                        </div>
                        <select 
                            value={buffer} 
                            disabled={reportStatus === 'completed'}
                            onChange={(e) => setBuffer(Number(e.target.value))}
                            style={{
                                background: 'rgba(255,255,255,0.05)',
                                color: 'white',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '4px',
                                padding: '0.4rem',
                                fontSize: '0.8rem',
                                outline: 'none',
                                opacity: reportStatus === 'completed' ? 0.5 : 1
                            }}
                        >
                            <option value={1}>0% (Exact Avg)</option>
                            <option value={1.1}>10% (Safe)</option>
                            <option value={1.25}>25% (Busy Day)</option>
                        </select>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <button 
                            onClick={handleGenerate} 
                            disabled={generating || reportStatus === 'completed'}
                            style={{
                                ...styles.primaryBtn,
                                background: reportStatus === 'completed' 
                                    ? 'rgba(255,255,255,0.05)' 
                                    : prepData.length > 0 
                                        ? 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)' 
                                        : styles.primaryBtn.background,
                                cursor: reportStatus === 'completed' ? 'not-allowed' : 'pointer',
                                opacity: generating ? 0.7 : 1
                            }}
                        >
                            <FaSync style={{ marginRight: '0.5rem', animation: generating ? 'spin 1s linear infinite' : 'none' }} />
                            {generating 
                                ? "Analyzing Sales..." 
                                : reportStatus === 'completed'
                                    ? "Forecast Locked"
                                    : prepData.length > 0 
                                        ? "Regenerate Forecast" 
                                        : "Generate Smart Forecast"}
                        </button>
                        {reportStatus !== 'completed' && (
                            <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
                                Forecasts are most accurate when generated once daily.
                            </span>
                        )}
                    </div>
                    {prepData.length > 0 && (
                        <button onClick={handlePrint} style={styles.secondaryBtn}>
                            <FaPrint style={{ marginRight: '0.5rem' }} /> Print
                        </button>
                    )}
                </div>
                </div>
            </div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                    <div style={{ 
                        width: '50px', 
                        height: '50px', 
                        border: '3px solid rgba(255, 65, 53, 0.2)',
                        borderTop: '3px solid #ff4135',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                </div>
            ) : (
                <>
                    {prepData.length === 0 ? (
                        <div style={styles.emptyState}>
                            <FaExclamationTriangle style={{ color: '#eab308' }} />
                            <h3>No Prep List Generated</h3>
                            <p>Click Generate to analyze sales and create today's prep list.</p>
                        </div>
                    ) : reportStatus === 'completed' ? (
                        <div style={{ ...styles.card, textAlign: 'center', padding: '3rem' }}>
                            <div style={{ width: '80px', height: '80px', background: 'rgba(0, 209, 178, 0.1)', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto 1.5rem auto' }}>
                                <FaRobot style={{ fontSize: '2.5rem', color: '#00d1b2' }} />
                            </div>
                            <h2>Today's Prep is Complete! 🎉</h2>
                            <p>All ingredients have been deducted from Inventory and archived for your records.</p>
                            <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                                <div><div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>ITEMS PREPPED</div><div style={{ fontSize: '1.5rem', color: '#00d1b2' }}>{prepData.length}</div></div>
                                <div><div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>STATUS</div><div style={{ fontSize: '1.5rem', color: '#00d1b2' }}>Archived</div></div>
                                <div><div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>DATE</div><div style={{ fontSize: '1.2rem' }}>{new Date(lastUpdated).toLocaleDateString()}</div></div>
                            </div>
                            <button onClick={handlePrint} style={styles.secondaryBtn}><FaPrint /> Print Summary</button>
                        </div>
                    ) : (
                        <div style={styles.card}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                                <div>
                                    <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>Last Updated: {new Date(lastUpdated).toLocaleString()}</span>
                                    <div style={{ color: '#f6ad55', fontSize: '0.8rem', fontWeight: '700' }}>STATUS: {reportStatus.toUpperCase()}</div>
                                </div>
                                <button onClick={handleFinalize} disabled={finalizing} style={{ ...styles.primaryBtn, background: '#00d1b2' }}>
                                    {finalizing ? "Finalizing..." : "Mark All as Done"}
                                </button>
                            </div>
                            <table style={styles.table}>
                                <thead style={styles.tableHeader}>
                                    <tr>
                                        <th style={styles.th}>Ingredient</th>
                                        <th style={styles.th}>Store Stock</th>
                                        <th style={styles.th}>Needed</th>
                                        <th style={styles.th}>Unit</th>
                                        <th style={styles.th}>Progress</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {prepData.map((item, index) => {
                                        const isLow = item.currentStock < item.predictedQuantity;
                                        return (
                                            <tr key={index} style={{ opacity: item.status === 'done' ? 0.6 : 1 }}>
                                                <td style={styles.td}>{item.name}</td>
                                                <td style={styles.td}>
                                                    <span style={{ color: isLow ? '#ff4d4d' : 'inherit' }}>{item.currentStock}</span>
                                                </td>
                                                <td style={styles.td}>
                                                    <span style={styles.quantity}>{item.predictedQuantity}</span>
                                                    {isLow && item.status !== 'done' && <span style={{ color: '#ff4d4d', fontSize: '0.6rem', marginLeft: '5px' }}>LOW</span>}
                                                </td>
                                                <td style={styles.td}>{item.unit}</td>
                                                <td style={styles.td}>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={item.status === 'done'}
                                                        onChange={() => handleItemStatusUpdate(item.inventoryId, item.status)}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                    
                    <div style={styles.infoGrid}>
                        <div style={styles.infoCard}>
                            <h4 style={styles.infoTitle}>🧠 Admin Knowledge</h4>
                            <p style={{ ...styles.infoText, color: '#ff4135', fontWeight: '800' }}>
                                NOTICE: Forecasts must be generated only once per day.
                            </p>
                            <p style={styles.infoText}>
                                Generating multiple times may overwrite current progress. Mark all as done at the end of the shift to sync inventory.
                            </p>
                        </div>
                        <div style={styles.infoCard}>
                            <h4 style={styles.infoTitle}>📋 Operational Guidelines</h4>
                            <ul style={{ ...styles.infoText, paddingLeft: '1.2rem', margin: '0.5rem 0' }}>
                                <li><strong>Best Timing:</strong> Generate the report every morning before the restaurant opens.</li>
                                <li><strong>Safety Buffer:</strong> Use 10% or 25% on weekends or public holidays.</li>
                                <li><strong>Process:</strong> Prep each item, then immediately check it off in the list.</li>
                                <li><strong>Finalization:</strong> Click 'Mark All as Done' only after the physical prep is complete.</li>
                            </ul>
                        </div>
                        <div style={styles.infoCard}>
                            <h4 style={styles.infoTitle}>💡 How this helps you</h4>
                            <p style={styles.infoText}>
                                Reduces ingredient waste by up to 15% and ensures you never run out of critical pre-prepped items during peak hours.
                            </p>
                        </div>
                    </div>
                </>
            )}

            <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>
            </div>
        </div>
    );
};

export default SmartPrep;
